// src/components/ui/BasicDialogCritical.jsx
import React, { useEffect, useRef } from "react";
import DeleteBackgroundCriticalIcon from "/icons/doctor/DeleteBackgroundCritical.svg";

export default function BasicDialogCritical(props) {
  const {
    open,
    title,
    body,
    cancelText = "Cancel",
    okText = "OK",
    onCancel,
    onOk,
    disableBackdropClose = false,
    iconSrc = DeleteBackgroundCriticalIcon,
    cancelStyle,
    okStyle,
  } = props;
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => dialogRef.current?.focus());
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !disableBackdropClose) {
        event.preventDefault();
        onCancel?.();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll("button:not([disabled])") || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [disableBackdropClose, onCancel, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center pointer-events-none"
      role="presentation"
    >
      {/* backdrop — don’t close if disabled */}
      <div
        className="absolute inset-0 bg-black/35 pointer-events-auto"
        onClick={disableBackdropClose ? undefined : onCancel}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="
          relative
          w-[calc(100vw-32px)] sm:w-[512px] max-w-[512px] min-w-[288px] min-h-[264px]
          bg-[rgb(var(--clr-surface-cont-highest))]
          rounded-[28px] overflow-hidden
          shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),
                  0px_6px_12px_-4px_rgba(29,79,215,0.06),
                  0px_3px_6px_-2px_rgba(26,27,35,0.06),
                  0px_3px_3px_-1px_rgba(26,27,35,0.06),
                  0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),
                  0px_0px_0px_1px_rgba(24,50,119,0.10)]
          pointer-events-auto
          focus:outline-none
        "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-1 flex flex-col items-center text-center">
          <img
            src={iconSrc}
            alt=""
            aria-hidden="true"
            className="w-12 h-12 mb-4"
          />
          <div className="text-headline-small text-on-surface">{title}</div>
          <div className="text-body-medium text-on-surface-variant mt-4">
            {body}
          </div>
        </div>

        <div className="px-6 pt-5 pb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Cancel (left) */}
          <button
            type="button"
            className="min-h-[48px] w-full rounded-xl px-4 py-3 text-label-large whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.24)]"
            style={{
              background: "var(--state-on-surface-08)",
              color: "var(--on-primary)",
              ...cancelStyle,
            }}
            onClick={onCancel}
          >
            {cancelText}
          </button>

          {/* Critical (right) */}
          <button
            type="button"
            className="min-h-[48px] w-full rounded-xl px-4 py-3 text-label-large whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(29,78,216,0.24)]"
            style={{
              background: "var(--state-critical-12)",
              color: "var(--on-critical)",
              ...okStyle,
            }}
            onClick={onOk}
          >
            {okText}
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}
